import { type Breakpoint } from '@divi/types';

import { type MultiViewData, type MultiViewUpdateDataCallback } from '../../types';
import { updateDataAttrs } from '../update-data-attrs';
import { updateDataClassName } from '../update-data-class-name';
import { updateDataContent } from '../update-data-content';
import { updateDataStyle } from '../update-data-style';
import { updateDataVisibility } from '../update-data-visibility';

// Match pattern: et_pb_*_N where N is one or more digits.
const MODULE_ORDER_CLASS_PATTERN = /^et_pb_.+_\d+$/;

/**
 * Extracts intermediate CSS selector parts from a multi-part MultiView setContent selector.
 *
 * MultiView selectors are space-separated paths from a module root to a target element.
 * Intermediate parts (everything except the first and last segment) identify child modules
 * that are intentional steps in that path—not foreign nested modules.
 *
 * @since ??
 *
 * @param {string[]} selectorParts Selector parts split from the MultiView setContent selector.
 *
 * @returns {string[]} Flat list of intermediate CSS selectors ready for element.matches().
 *
 * @example
 * ```ts
 * getIntermediateSelectorParts([
 *   '.et_pb_timeline_0.et_pb_timeline',
 *   '.et_pb_timeline_item_0',
 *   '.et_pb_timeline_content',
 * ]);
 * // ['.et_pb_timeline_item_0']
 * ```
 */
const getIntermediateSelectorParts = (selectorParts: string[]): string[] => {
  const intermediateParts = selectorParts.slice(1, -1);
  const normalizedParts: string[] = [];

  intermediateParts.forEach(part => {
    part.split(',').forEach(segment => {
      const trimmedSegment = segment.trim().replace(/,\s*$/, '');

      if (trimmedSegment) {
        normalizedParts.push(trimmedSegment);
      }
    });
  });

  return normalizedParts;
};

/**
 * Returns whether an ancestor element is an intentional intermediate module in the selector path.
 *
 * @since ??
 *
 * @param {HTMLElement} ancestorElement The ancestor element being evaluated.
 * @param {string[]} intermediateSelectorParts Intermediate selector parts from getIntermediateSelectorParts().
 *
 * @returns {boolean} Whether the ancestor matches an intentional intermediate selector part.
 */
const isIntentionalIntermediateModule = (ancestorElement: HTMLElement, intermediateSelectorParts: string[]): boolean =>
  intermediateSelectorParts.some(segment => {
    try {
      return ancestorElement.matches(segment);
    } catch {
      return false;
    }
  });

/**
 * Returns whether a setContent target belongs to the module scope and is not inside a foreign nested module.
 *
 * @since ??
 *
 * @param {HTMLElement} element The content element matched by the MultiView selector.
 * @param {string[]} selectorParts Selector parts split from the MultiView setContent selector.
 *
 * @returns {boolean} Whether the element should receive setContent updates.
 */
const isSetContentElementInModuleScope = (element: HTMLElement, selectorParts: string[]): boolean => {
  // Extract module class (e.g., '.et_pb_text_0' from '.et_pb_text_0 .et_pb_text_inner').
  const moduleSelector = selectorParts[0];

  // Find the closest ancestor that matches the module selector.
  const moduleElement = element.closest(moduleSelector);

  if (!moduleElement) {
    return false;
  }

  const intermediateSelectorParts = getIntermediateSelectorParts(selectorParts);

  // Walk up the DOM tree from element to moduleElement and check if any intermediate
  // element has a module class (pattern: et_pb_*_N where N is a number). If found,
  // it means this content element belongs to a nested module.
  let current: HTMLElement | null = element.parentElement;

  while (current && current !== moduleElement) {
    const ancestorElement = current;

    // Check if current element has a module class (e.g., et_pb_text_1, et_pb_blurb_2).
    // Module classes follow the pattern: et_pb_<module>_<instanceNumber>
    const { classList } = ancestorElement;
    const isIntentionalModule = isIntentionalIntermediateModule(ancestorElement, intermediateSelectorParts);

    for (let classIndex = 0; classIndex < classList.length; classIndex++) {
      const className = classList[classIndex];

      // Match pattern: et_pb_*_N where N is one or more digits.
      if (!MODULE_ORDER_CLASS_PATTERN.test(className)) {
        continue;
      }

      if (!isIntentionalModule) {
        // Found a module element between content element and target module - this is nested.
        return false;
      }

      break;
    }

    current = current.parentElement;
  }

  // No nested module found - this element belongs to the target module.
  return true;
};

/**
 * Update elements.
 *
 * This is function that takes in an array of data and a breakpoint, and updates the data
 * attributes, class name, content, style, or visibility based on the specified action for each item in
 * the array.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to update the elements.
 * @param {MultiViewData[]} params.items An array of objects containing data for multiple views.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint, which can be 'phone', 'tablet', or 'desktop'.
 * @param {boolean} params.isInit Whether this is the first time the function is being called.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The names of the breakpoints.
 */
export const updateData: MultiViewUpdateDataCallback = ({
  items,
  breakpoint,
  isInit = false,
  baseBreakpoint,
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  items: MultiViewData[];
  breakpoint: Breakpoint.Name;
  isInit?: boolean;
  baseBreakpoint?: Breakpoint.Name;
  breakpointNames?: Breakpoint.Names;
}): void => {
  items.forEach((item, itemIndex) => {
    const { action, data, moduleId, moduleName, selector, hoverSelector, switchOnLoad } = item;
    const elements = document.querySelectorAll<HTMLElement>(selector);

    switch (action) {
      case 'setAttrs': {
        elements.forEach(element => {
          updateDataAttrs({
            item: {
              action,
              data,
              moduleId,
              moduleName,
              element,
              hoverElements: hoverSelector ? document.querySelectorAll<HTMLElement>(hoverSelector) : [element],
              switchOnLoad,
            },
            listenerNamespace: `update-data-attrs:${moduleId}:${itemIndex}`,
            breakpoint,
            isInit,
            baseBreakpoint,
            breakpointNames,
          });
        });
        break;
      }

      case 'setClassName': {
        elements.forEach(element => {
          updateDataClassName({
            item: {
              action,
              data,
              moduleId,
              moduleName,
              element,
              hoverElements: hoverSelector ? document.querySelectorAll<HTMLElement>(hoverSelector) : [element],
              switchOnLoad,
            },
            breakpoint,
            isInit,
            baseBreakpoint,
            breakpointNames,
          });
        });
        break;
      }

      case 'setContent': {
        // Filter elements to prevent nested modules from being incorrectly updated.
        // Single-part selectors are not filtered for compatibility.
        const selectorParts = selector.trim().split(/\s+/);
        const filteredElements =
          selectorParts.length > 1
            ? Array.from(elements).filter(element => isSetContentElementInModuleScope(element, selectorParts))
            : elements;

        filteredElements.forEach(element => {
          updateDataContent({
            item: {
              action,
              data,
              moduleId,
              moduleName,
              element,
              hoverElements: hoverSelector ? document.querySelectorAll<HTMLElement>(hoverSelector) : [element],
              switchOnLoad,
            },
            breakpoint,
            isInit,
            baseBreakpoint,
            breakpointNames,
          });
        });
        break;
      }

      case 'setStyle': {
        elements.forEach(element => {
          updateDataStyle({
            item: {
              action,
              data,
              moduleId,
              moduleName,
              element,
              hoverElements: hoverSelector ? document.querySelectorAll<HTMLElement>(hoverSelector) : [element],
              switchOnLoad,
            },
            breakpoint,
            isInit,
            baseBreakpoint,
            breakpointNames,
          });
        });
        break;
      }

      case 'setVisibility': {
        elements.forEach(element => {
          updateDataVisibility({
            item: {
              action,
              data,
              moduleId,
              moduleName,
              element,
              hoverElements: hoverSelector ? document.querySelectorAll<HTMLElement>(hoverSelector) : [element],
              switchOnLoad,
            },
            breakpoint,
            isInit,
            baseBreakpoint,
            breakpointNames,
          });
        });
        break;
      }

      // Nothing to do if the action is not recognized.
      default:
    }
  });
};
